import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ShieldCheck, X } from "lucide-react";
import { saveConsent, isConsentValid } from "../lib/cookieConsent";

export default function CookieConsentBanner({ settings, consent, onConsentSaved }) {
  const [openDetails, setOpenDetails] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (consent && isConsentValid(settings, consent)) {
      setAnalytics(Boolean(consent.analytics));
      setMarketing(Boolean(consent.marketing));
    } else {
      setAnalytics(false);
      setMarketing(false);
    }
  }, [consent, settings]);

  if (!settings?.enabled || isConsentValid(settings, consent)) return null;

  const acceptAll = () => {
    const next = saveConsent(settings.consent_version, {
      analytics: true,
      marketing: true,
    });
    onConsentSaved?.(next);
    setOpenDetails(false);
  };

  const rejectAll = () => {
    const next = saveConsent(settings.consent_version, {
      analytics: false,
      marketing: false,
    });
    onConsentSaved?.(next);
    setOpenDetails(false);
  };

  const savePreferences = () => {
    const next = saveConsent(settings.consent_version, {
      analytics,
      marketing,
    });
    onConsentSaved?.(next);
    setOpenDetails(false);
  };

  return (
    <>
      <div className="fixed inset-x-0 bottom-0 z-50 p-4 sm:p-6">
        <div className="max-w-5xl mx-auto bg-slate-900 text-white shadow-2xl rounded-2xl border border-slate-700 overflow-hidden">
          <div className="grid lg:grid-cols-[1.2fr_0.8fr]">
            <div className="p-5 sm:p-6">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-sky-300 font-bold mb-3">
                <ShieldCheck size={14} /> Cookie consent
              </div>
              <h2 className="font-display text-2xl sm:text-3xl font-medium tracking-tight mb-3">{settings.banner_title}</h2>
              <p className="text-sm sm:text-base text-slate-300 leading-relaxed max-w-2xl">
                {settings.banner_description}
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
                <button type="button" onClick={acceptAll} className="px-4 py-2.5 rounded-full bg-white text-slate-900 font-semibold hover:bg-slate-100">
                  {settings.accept_button_text}
                </button>
                {settings.show_reject_button && (
                  <button type="button" onClick={rejectAll} className="px-4 py-2.5 rounded-full border border-slate-600 text-white font-semibold hover:bg-slate-800">
                    {settings.reject_button_text}
                  </button>
                )}
                {settings.show_customize_button && (
                  <button type="button" onClick={() => setOpenDetails(true)} className="px-4 py-2.5 rounded-full border border-sky-400/50 text-sky-100 font-semibold hover:bg-slate-800">
                    {settings.customize_button_text}
                  </button>
                )}
                <Link to={settings.privacy_policy_url || "/politica-de-confidentialitate"} className="text-slate-300 hover:text-white underline underline-offset-4">
                  Politica de confidențialitate
                </Link>
              </div>
            </div>

            <div className="bg-slate-950/70 p-5 sm:p-6 border-t lg:border-t-0 lg:border-l border-slate-800">
              <div className="text-xs uppercase tracking-[0.18em] text-slate-400 font-bold mb-4">Preferințe</div>
              <div className="space-y-3 text-sm">
                <ConsentRow
                  label={settings.necessary_label}
                  description={settings.necessary_description}
                  checked
                  disabled
                />
                {settings.analytics_enabled && (
                  <ConsentRow
                    label={settings.analytics_label}
                    description={settings.analytics_description}
                    checked={analytics}
                    onChange={setAnalytics}
                  />
                )}
                {settings.marketing_enabled && (
                  <ConsentRow
                    label={settings.marketing_label}
                    description={settings.marketing_description}
                    checked={marketing}
                    onChange={setMarketing}
                  />
                )}
              </div>
              {settings.show_customize_button && (
                <button type="button" onClick={savePreferences} className="mt-5 w-full px-4 py-2.5 rounded-full bg-sky-500 text-slate-950 font-semibold hover:bg-sky-400">
                  {settings.save_button_text}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {openDetails && (
        <div className="fixed inset-0 z-[60] bg-slate-950/65 backdrop-blur-sm p-4 flex items-end sm:items-center justify-center">
          <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-bold">Setări cookie-uri</div>
                <h3 className="font-display text-2xl font-medium text-slate-900 mt-1">Alege ce permiți</h3>
              </div>
              <button type="button" onClick={() => setOpenDetails(false)} className="w-9 h-9 rounded-full hover:bg-slate-100 inline-flex items-center justify-center text-slate-600">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <ConsentRow
                label={settings.necessary_label}
                description={settings.necessary_description}
                checked
                disabled
              />
              {settings.analytics_enabled && (
                <ConsentRow
                  label={settings.analytics_label}
                  description={settings.analytics_description}
                  checked={analytics}
                  onChange={setAnalytics}
                />
              )}
              {settings.marketing_enabled && (
                <ConsentRow
                  label={settings.marketing_label}
                  description={settings.marketing_description}
                  checked={marketing}
                  onChange={setMarketing}
                />
              )}
              <div className="pt-2 flex flex-wrap gap-3 justify-end">
                {settings.show_reject_button && (
                  <button type="button" onClick={rejectAll} className="px-4 py-2.5 rounded-full border border-slate-300 text-slate-700 font-medium hover:bg-slate-50">
                    {settings.reject_button_text}
                  </button>
                )}
                <button type="button" onClick={savePreferences} className="px-4 py-2.5 rounded-full bg-slate-900 text-white font-medium hover:bg-slate-700">
                  {settings.save_button_text}
                </button>
                <button type="button" onClick={acceptAll} className="px-4 py-2.5 rounded-full bg-sky-600 text-white font-medium hover:bg-sky-700 inline-flex items-center gap-2">
                  <Check size={14} /> {settings.accept_button_text}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ConsentRow({ label, description, checked, onChange, disabled }) {
  return (
    <label className={`flex items-start gap-3 p-3 rounded-xl border ${checked ? "border-sky-400 bg-sky-500/10" : "border-slate-200 bg-white"}`}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
        className="mt-1 rounded border-slate-300 text-sky-600 focus:ring-sky-500 disabled:opacity-70"
      />
      <span className="min-w-0">
        <span className="block font-medium text-slate-900">{label}</span>
        <span className="block text-sm text-slate-600 leading-relaxed">{description}</span>
      </span>
    </label>
  );
}
